import React, { useEffect, useState, useCallback } from "react";
import { DialogBox } from "../../../../Components/Dialog/DialogBox";
import { braillerRepairDetailsApi } from "../../../PackingList/Braillers/braillerRepairDetailsApi";
import { ButtonIconPrint, ButtonIconCopyClipboard } from "../../../../Components/Inputs";
import {emptyGuid} from "@utilities/commonUtilities";
import type { HandleApiCall } from "../../../../types";
import type { BraillerRepairRequestDetails } from "../brailler.types";

interface BraillerRepairDetailsModalProps {
    braillerLoanId: string | null;
    braillerRepairId: string | null;
    isOpen: boolean;
    onClose: () => void;
    handleApiCall: HandleApiCall;
}

export const BraillerRepairDetailsModal = ({
    braillerLoanId,
    braillerRepairId,
    isOpen,
    onClose,
    handleApiCall,
}: BraillerRepairDetailsModalProps) => {
    const [repairDetails, setRepairDetails] = useState<BraillerRepairRequestDetails | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [copySuccess, setCopySuccess] = useState(false);

    useEffect(() => {
        const hasValidLoanId = braillerLoanId && braillerLoanId !== emptyGuid;
        const hasValidRepairId = braillerRepairId && braillerRepairId !== emptyGuid;
        
        if (isOpen && (hasValidLoanId || hasValidRepairId)) {
            setIsLoading(true);
            setCopySuccess(false);
            handleApiCall(
                () => braillerRepairDetailsApi.getBraillerRepairRequestDetails(braillerLoanId!, braillerRepairId),
                (result: BraillerRepairRequestDetails) => {
                    setRepairDetails(result);
                    setIsLoading(false);
                },
                () => {
                    setIsLoading(false);
                }
            );
        } else if (!isOpen) {
            setRepairDetails(null);
            setIsLoading(false);
            setCopySuccess(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, braillerLoanId, braillerRepairId]);

    const handleCopyToClipboard = useCallback(async () => {
        if (!repairDetails) return;

        try {
            const textToCopy = repairDetails.formattedText;
            await navigator.clipboard.writeText(textToCopy);
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 3000);
        } catch (err) {
            const textArea = document.createElement('textarea');
            textArea.value = repairDetails.formattedText;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.select();
            try {
                document.execCommand('copy');
                setCopySuccess(true);
                setTimeout(() => setCopySuccess(false), 3000);
            } catch (error) {
                setCopySuccess(false);
            }
            document.body.removeChild(textArea);
        }
    }, [repairDetails]);


    const handlePrint = useCallback(() => {
        if (!repairDetails) return;

        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        const dateCreated = repairDetails.dateCreated 
            ? new Date(repairDetails.dateCreated).toLocaleDateString()
            : '';

        const printContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <title>Brailler Repair Request Details</title>
                <style>
                    body {
                        font-family: Arial, sans-serif;
                        padding: 40px;
                        line-height: 1.6;
                    }
                    h1 {
                        color: #333;
                        border-bottom: 2px solid #333;
                        padding-bottom: 10px;
                    }
                    .content {
                        white-space: pre-wrap;
                        margin: 20px 0;
                    }
                    .date {
                        margin-top: 20px;
                        padding-top: 10px;
                        border-top: 1px solid #ddd;
                        font-weight: bold;
                    }
                    @media print {
                        body {
                            padding: 20px;
                        }
                    }
                </style>
            </head>
            <body>
                <h1>Brailler Repair Request Details</h1>
                <div class="content">${repairDetails.formattedText.replace(/\n/g, '<br>')}</div>
                ${dateCreated ? `<div class="date">Date Created: ${dateCreated}</div>` : ''}
            </body>
            </html>
        `;

        printWindow.document.write(printContent);
        printWindow.document.close();
        
        printWindow.onload = () => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        };
    }, [repairDetails]);

    const handleClose = () => {
        onClose();
    };

    if (!isOpen) return null;

    return (
        <DialogBox
            openDialog={isOpen}
            handleCancel={handleClose}
            handleSave={handleClose}
            saveLabel="Close"
            cancelLabel=""
            title="Brailler Details"
        >
            <div style={{ minHeight: '200px' }}>
                {isLoading && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        Loading repair details...
                    </div>
                )}

                {!isLoading && repairDetails && (
                    <div>
                        <div style={{ marginBottom: '20px', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                            <ButtonIconPrint
                                id="btnPrintRepairDetails"
                                inputDescription="Print details"
                                onClick={handlePrint}
                                disabled={!repairDetails.formattedText}
                            />
                            <ButtonIconCopyClipboard
                                id="btnCopyToClipboard"
                                inputDescription={copySuccess ? "Copied to clipboard!" : "Copy to clipboard"}
                                onClick={handleCopyToClipboard}
                                disabled={!repairDetails.formattedText}
                            />
                        </div>

                        <div style={{ whiteSpace: 'pre-wrap', marginBottom: '20px', marginTop: '20px' }}>
                            {repairDetails.formattedText}
                        </div>

                        {repairDetails.dateCreated && (
                            <div style={{ marginTop: '20px', borderTop: '1px solid #ddd', paddingTop: '10px' }}>
                                <strong>Date Created:</strong>{' '}
                                {new Date(repairDetails.dateCreated).toLocaleDateString()}
                            </div>
                        )}
                    </div>
                )}

                {!isLoading && !repairDetails && (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#666' }}>
                        No details available.
                    </div>
                )}
            </div>
        </DialogBox>
    );
};
